/**
 * "Rebuild from documents" confirmation (redesign rationale §2g).
 *
 * Replaces the old `window.confirm`, and — more importantly — moves the consent
 * to the FRONT of the action. The previous flow POSTed without `refresh`, then
 * tried to detect a skipped sheet in the response to decide whether to ask; it
 * read `res.scopes` where the backend returns `res.results`, so the prompt never
 * fired and a rebuild over a filled sheet silently no-oped.
 *
 * Here the operator consents first and the POST carries `{refresh: true}` from
 * the outset, so there is no second round trip and no response shape to misread.
 * An empty sheet skips the dialog entirely — nothing to lose.
 */
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

import Txt from "../../../../components/typography/Txt";
import { T } from "./budgetDrawsTheme";

const RebuildSheetDialog = ({ open, lineCount = 0, busy, onCancel, onConfirm }) => (
  <Dialog
    open={open}
    onClose={busy ? undefined : onCancel}
    maxWidth="xs"
    fullWidth
    PaperProps={{ sx: { borderRadius: "12px" } }}
  >
    <DialogTitle sx={{ fontSize: 15.5, fontWeight: 700, pb: 1 }}>
      Replace the current sheet?
    </DialogTitle>
    <DialogContent sx={{ pb: 1 }}>
      <Txt sx={{ fontSize: 12, color: T.muted }}>
        This sheet has {lineCount} line {lineCount === 1 ? "item" : "items"}. Rebuilding from
        the loan&apos;s documents replaces every row with the extracted budget. Any
        hand-entered changes are lost.
      </Txt>
    </DialogContent>
    <DialogActions sx={{ p: "8px 20px 18px", gap: 1 }}>
      <Button variant="text" onClick={onCancel} disabled={busy} sx={{ textTransform: "none" }}>
        Keep my sheet
      </Button>
      <Button
        variant="contained"
        disableElevation
        onClick={onConfirm}
        disabled={busy}
        sx={{ textTransform: "none", bgcolor: T.ink, "&:hover": { bgcolor: T.ink2 } }}
      >
        {busy ? "Rebuilding…" : "Rebuild sheet"}
      </Button>
    </DialogActions>
  </Dialog>
);

export default RebuildSheetDialog;
